import { useMutation } from '@apollo/client';
import { ZonedDateTime } from '@js-joda/core';
import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import React, { useCallback, useMemo, useState } from 'react';
import { compose, withProps } from 'recompose';
import { setPatientEventTimestamp } from '../../../../graph/procedureSteps';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import { withLabel } from '@/se/components/Label';
import { RouterlessModal } from '@/se/components/Modal';
import { CancelableForm, DateInput, formatTime } from '../../../inputs/timeEdit/TimeEditForm';
import DateDropdownInput from '@/se/components/inputs/DateInput';
import { DelayReasonSchemaEntry } from '../delayReasons/DelayReasonInput';
import { delayReasonMutationConfig, milestoneFromEventType } from '../delayReasons/delayReasons';
import { applyEditedWallClock } from './eventTimestamp';

const getEventType = event => {
  switch (event?.type) {
    case 'Created':
    case 'Discharged':
    case 'BlockNerve':
    case 'CleaningCompleted':
    case 'OrRoomReady':
    case 'BecameReadyInPREP':
    case 'ReadyForAnesthesia':
    case 'ReadyForSurgeon':
      return { __type: `${event?.type}$` };
    case 'TransferredToChair':
    case 'TransferredToPT':
    case 'PTComplete':
      return { __type: `${event?.type}`, manualEntryBy: null, manualTimestamp: null };
    case 'Entered':
      return { __type: 'Entered', roomId: event?.room?.id, manualEntryBy: null, manualTimestamp: null };
    case 'Exited':
      return { __type: 'Exited', roomId: event?.room?.id, manualEntryBy: null };
    case 'ProcedureStatusSet':
      return {
        __type: 'ProcedureStatusSet',
        status: { [event?.status]: {} },
        manualEntryBy: null,
        manualTimestamp: null,
      };
    default:
      return null;
  }
};

export const FormInput = (eventText, withDate = false, options: any = {}) =>
  withProps({
    schema: {
      ...(withDate && {
        date: compose(
          withLabel('Date'),
          withProps({
            defaultDate: options.defaultDate,
          })
        )(DateDropdownInput),
      }),
      time: compose(
        withLabel(eventText),
        withProps({
          autoFocus: true,
          required: true,
        })
      )(DateInput),
      ...(options.reasonDelayType && {
        reason: DelayReasonSchemaEntry(options.reasonDelayType),
      }),
    },
  })(ObjectInput);

const PatientEventEdit = ({ patientId, event, eventText, editable = false, patient = undefined }) => {
  const [edit, setEdit] = useState(false);
  const [save] = useMutation(setPatientEventTimestamp);
  const [saveOobReason] = useMutation(delayReasonMutationConfig.oob.mutation);
  const [savePtReason] = useMutation(delayReasonMutationConfig.pt.mutation);
  const [savePtCompleteReason] = useMutation(delayReasonMutationConfig.ptComplete.mutation);
  const [savePacuOutReason] = useMutation(delayReasonMutationConfig.pacuOut.mutation);
  const reasonMutations = useMemo(
    () => ({
      oob: saveOobReason,
      pt: savePtReason,
      ptComplete: savePtCompleteReason,
      pacuOut: savePacuOutReason,
    }),
    [saveOobReason, savePtReason, savePtCompleteReason, savePacuOutReason]
  );
  const delayType = milestoneFromEventType[event?.type];
  const includeDate = event?.type === 'Discharged';

  const toggleEdit = useCallback(() => {
    setEdit(prev => !prev);
  }, []);

  const saveTimestamp = useCallback(
    async (time, date) => {
      if (!time) {
        toggleEdit();
        return;
      }
      const eventType = getEventType(event);
      const newDateTime = applyEditedWallClock(event?.timestamp, time, date);
      newDateTime.toISOString() !== new Date(event?.timestamp).toISOString() &&
        patientId &&
        eventType &&
        (await save({
          variables: {
            patientId,
            eventType: JSON.stringify(eventType),
            timestamp: newDateTime,
          },
        }));
      toggleEdit();
    },
    [event, patientId, save, toggleEdit]
  );

  const saveReason = useCallback(
    async reason => {
      if (!delayType || !patientId) return;
      const { variableName, patientField } = delayReasonMutationConfig[delayType];
      const initialReason = patient?.[patientField] ?? null;
      const nextReason = reason ?? null;
      if (nextReason !== initialReason) {
        await reasonMutations[delayType]({
          variables: { id: patientId, [variableName]: nextReason },
        });
      }
    },
    [delayType, patient, patientId, reasonMutations]
  );

  const eventZdt = useMemo(
    () => (event?.timestamp ? ZonedDateTime.parse(event.timestamp) : undefined),
    [event?.timestamp]
  );
  const initialTime = useMemo(
    () => (eventZdt ? formatTime(eventZdt.toLocalDateTime().toString()) : undefined),
    [eventZdt]
  );
  const initialDate = useMemo(() => (eventZdt ? eventZdt.toLocalDate().toString() : undefined), [eventZdt]);

  const onSubmit = useCallback(
    async val => {
      // Only save the timestamp when the user actually edited the form strings,
      // so reason-only submits never rewrite the event time.
      const timeEdited = val?.time !== initialTime || (includeDate && val?.date !== initialDate);
      if (timeEdited) {
        await saveTimestamp(val?.time, val?.date);
      } else {
        toggleEdit();
      }
      await saveReason(val?.reason);
    },
    [saveTimestamp, saveReason, initialTime, initialDate, includeDate, toggleEdit]
  );
  const defaultDate = useMemo(() => (event?.timestamp ? new Date(event.timestamp) : undefined), [event?.timestamp]);
  const inputComponent = useMemo(
    () => FormInput(eventText, includeDate, { defaultDate, reasonDelayType: delayType }),
    [eventText, includeDate, defaultDate, delayType]
  );

  return (
    <>
      {editable && (
        <IconButton onClick={toggleEdit} size="small" color="primary">
          <EditIcon />
        </IconButton>
      )}
      {editable && edit && (
        <RouterlessModal title="Update Time" onClose={toggleEdit}>
          <CancelableForm
            autoFocus
            label="Update"
            onClose={toggleEdit}
            initialValue={{
              time: initialTime,
              ...(includeDate ? { date: initialDate } : {}),
              ...(delayType ? { reason: patient?.[delayReasonMutationConfig[delayType].patientField] ?? null } : {}),
            }}
            input={inputComponent}
            onSubmit={onSubmit}
            busy={false}
          />
        </RouterlessModal>
      )}
    </>
  );
};

export default PatientEventEdit;
